import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';
import { useGuardian } from '../contexts/GuardianContext';

const {
  FiPlus, FiEdit3, FiTrash2, FiMail, FiPhone, FiMapPin,
  FiX, FiSave, FiUser, FiBriefcase
} = FiIcons;

function ContactManager({ contacts, schools, onContactsUpdated }) {
  const { getActiveUserId } = useGuardian();
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    title: '',
    email: '',
    phone: '',
    department: 'Athletics',
    saved_school_id: ''
  });

  const handleAdd = () => {
    setEditingId(null);
    setForm({
      first_name: '',
      last_name: '',
      title: '',
      email: '',
      phone: '',
      department: 'Athletics',
      saved_school_id: ''
    });
    setShowModal(true);
  };

  const handleEdit = (contact) => {
    setEditingId(contact.id);
    setForm({
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      title: contact.title || '',
      email: contact.email || '',
      phone: contact.phone || '',
      department: contact.department || 'Athletics',
      saved_school_id: contact.saved_school_id || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      const { error } = await supabase.from('contacts').delete().eq('id', id);
      if (error) throw error;
      onContactsUpdated();
    } catch (err) {
      console.error('Error deleting contact:', err);
      alert('Failed to delete contact.');
    }
  };

  const handleSave = async () => {
    if (!form.first_name || !form.last_name) {
      alert('First and Last name are required.');
      return;
    }
    try {
      setSaving(true);
      const activeUserId = getActiveUserId();
      
      const payload = {
        user_id: activeUserId,
        first_name: form.first_name,
        last_name: form.last_name,
        title: form.title,
        email: form.email,
        phone: form.phone,
        department: form.department,
        saved_school_id: form.saved_school_id || null
      };

      if (editingId) {
        const { error } = await supabase.from('contacts').update(payload).eq('id', editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('contacts').insert(payload);
        if (error) throw error;
      }

      setShowModal(false);
      onContactsUpdated();
    } catch (err) {
      console.error('Error saving contact:', err);
      alert('Failed to save contact.');
    } finally {
      setSaving(false);
    }
  };

  const getSchoolName = (savedSchoolId) => {
    if (!savedSchoolId) return 'No School Linked';
    const school = schools.find(s => s.saved_id === savedSchoolId);
    return school ? school.name : 'Unknown School';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-space-grotesk font-semibold text-secondary">Coach & Staff Contacts</h3>
          <p className="text-sm text-gray-500">Manage your recruiting network</p>
        </div>
        <button 
          onClick={handleAdd}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 text-sm font-medium"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add Contact</span>
        </button>
      </div>

      {contacts.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
          <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiUser} className="w-8 h-8 text-primary" />
          </div>
          <h4 className="text-lg font-semibold text-gray-800 mb-2">No Contacts Yet</h4>
          <p className="text-gray-500 mb-6">Start building your network by adding college coaches and recruiting coordinators.</p>
          <button onClick={handleAdd} className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            Add Your First Contact
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {contacts.map((contact) => (
            <div key={contact.id} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-all">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-primary font-bold">
                    {contact.first_name[0]}{contact.last_name[0]}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">{contact.first_name} {contact.last_name}</h4>
                    <p className="text-sm text-gray-500">{contact.title || 'Coach'}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button onClick={() => handleEdit(contact)} className="text-gray-400 hover:text-primary transition-colors p-1">
                    <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(contact.id)} className="text-gray-400 hover:text-red-500 transition-colors p-1">
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="space-y-2 mt-4 text-sm text-gray-600">
                {contact.saved_school_id && (
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiMapPin} className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-800">{getSchoolName(contact.saved_school_id)}</span>
                  </div>
                )}
                {contact.email && (
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiMail} className="w-4 h-4 text-gray-400" />
                    <a href={`mailto:${contact.email}`} className="hover:text-primary transition-colors">{contact.email}</a>
                  </div>
                )}
                {contact.phone && (
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiPhone} className="w-4 h-4 text-gray-400" />
                    <a href={`tel:${contact.phone}`} className="hover:text-primary transition-colors">{contact.phone}</a>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setShowModal(false)}
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-space-grotesk font-bold text-secondary">
                    {editingId ? 'Edit Contact' : 'Add New Contact'}
                  </h2>
                  <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                    <SafeIcon icon={FiX} className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                      <input type="text" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                      <input type="text" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary outline-none" />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Linked School</label>
                    <select value={form.saved_school_id} onChange={(e) => setForm({...form, saved_school_id: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary outline-none">
                      <option value="">-- No School Linked --</option>
                      {schools.map(s => (
                        <option key={s.saved_id} value={s.saved_id}>{s.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title/Role</label>
                      <input type="text" placeholder="e.g., Head Coach" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                      <input type="text" placeholder="e.g., Athletics" value={form.department} onChange={(e) => setForm({...form, department: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary outline-none" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary outline-none" />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-8">
                  <button onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
                  <button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2 disabled:opacity-50">
                    <SafeIcon icon={FiSave} className="w-4 h-4" />
                    <span>{saving ? 'Saving...' : 'Save Contact'}</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ContactManager;